import ClockCircleOutlined from '@ant-design/icons/lib/icons/ClockCircleOutlined';
import { ConfigProvider, Form, Row, Select, Space, Tooltip } from 'antd';
import { ValidateStatus } from 'antd/lib/form/FormItem';
import locale from 'antd/lib/locale/en_US';
import { isNil, noop, range } from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import { httpCreators, MenuValidator, Tags } from 'rbh-merchant-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AuthHelper } from '../../../../../auth/auth-helper';
import { generalCreators } from '../../../../../redux/general/general-action';
import { DATE_PICKER_FORM, MODAL_TYPE } from '../../../../../redux/general/general-constant';
import { GENERAL_STATE_KEY } from '../../../../../redux/general/general-reducer';
import {
  RBHBox,
  RBHButton,
  RBHDatePicker,
  RBHImage,
  RBHSelect,
  RBHSwitch,
  RBHText,
  RBHTextInput,
  RBHTextInputArea
} from '../../../../../shared/components';
import styleButton from '../../../../../shared/components/rbh-button/styles';
import styleImage from '../../../../../shared/components/rbh-image/styles';
import styleText from '../../../../../shared/components/rbh-text/styles';
import styleTextInput from '../../../../../shared/components/rbh-textinput/styles';
import { TIME_24 } from '../../../../../shared/helpers/constant';
import { crop } from '../../../../../shared/helpers/crop';
import { UtilHelper } from '../../../../../shared/helpers/utility';
import { MartProduct } from '../../../../../shared/models/mart-product';
import { Images } from '../../../../../shared/theme';
import { BUSINESS_TYPE, MERCHANT_TYPE } from '../../../../main-constant';
import { MART_CATEGORY_REQUEST_ID_LIST } from '../../../MartCategory/redux/martCategory-constant';
import { MART_CATEGORY_STATE_KEY, martCategoryStateSelector } from '../../../MartCategory/redux/martCategory-reducer';
import { martCategorySagas } from '../../../MartCategory/redux/martCategory-saga';
import { MartCategoryService } from '../../../MartCategory/redux/martCategory-service';
import { mainStyles } from '../../../styles';
import { martProductCreators } from '../../redux/martProduct-action';
import { MART_PRODUCT_INLINE_ERROR, MART_PRODUCT_REQUEST_ID_LIST, MART_PRODUCT_STATUS, PRODUCT_PRICE_MAXLENGTH, PRODUCT_PRICE_REGEX, PROMOTION_STATUS } from '../../redux/martProduct-constant';
import { MART_PRODUCT_STATE_KEY, martProductStateSelector } from '../../redux/martProduct-reducer';
import { martProductSagas } from '../../redux/martProduct-saga';
import { MartProductService } from '../../redux/martProduct-service';
import './style.css';
import styles, { layout } from './styles';

export const FormAddEdit: React.FC<Props> = (props) => {
  let product: MartProduct | undefined;
  if (props.editData) {
    let modelData = { ...props.editData };
    if (props.editData?.originalProductPrice === props.editData?.productPrice) {
      modelData = { ...modelData, productPrice: undefined };
    } else if (!!props.editData?.promoStartDateTime || props.editData?.promoEndDateTime) {
      const promoStartDateTime = moment(props.editData?.promoStartDateTime).format('YYYY-MM-DD HH:mm').split(' ');
      const promoEndDateTime = moment(props.editData?.promoEndDateTime).format('YYYY-MM-DD HH:mm').split(' ');
      modelData = {
        ...modelData,
        promoStartDate: moment(promoStartDateTime[0]),
        promoStartTime: promoStartDateTime[1],
        promoEndDate: moment(promoEndDateTime[0]),
        promoEndTime: promoEndDateTime[1]
      };
    }
    product = new MartProduct(modelData);
  }

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [productStatus, setproductStatus] = useState(product ? product.productStatus === MART_PRODUCT_STATUS.ACTIVE : true);
  const [productId] = React.useState(product ? product.productId : undefined);
  const [productCategoryId] = React.useState(product ? product.productCategory?.productCategoryId : undefined);
  const [category] = React.useState(product ? product.productCategory?.productCategoryNameTH : undefined);
  const [imageUpload, setImageUpload] = useState(product ? product.productPhoto : null);
  const [lastFile, setLastFile] = useState(undefined);
  const inputRef = useRef<any>(null);
  const [formInvalid, setFormInvalid] = useState(!product);
  const martTag = useSelector(martCategoryStateSelector(MART_CATEGORY_STATE_KEY.TAG_LIST)) as Tags[];
  const martCategory = useSelector(martCategoryStateSelector(MART_CATEGORY_STATE_KEY.CATEGORY_LIST)) as [];
  const isPOSActioned = useSelector(martProductStateSelector(MART_PRODUCT_STATE_KEY.IS_POS_ACTIONED)) as boolean;
  const calculatedSize = useSelector(martProductStateSelector(MART_PRODUCT_STATE_KEY.PRODUCT_CALCULATED_SIZE)) as any;
  const calculatedWeight = useSelector(martProductStateSelector(MART_PRODUCT_STATE_KEY.PRODUCT_CALCULATED_WEIGHT)) as any;
  const productWeightSizeData = useSelector(martProductStateSelector(MART_PRODUCT_STATE_KEY.PRODUCT_WEIGHT_SIZE_DATA)) as any;
  const isEditBasketLimit = useSelector(martProductStateSelector(MART_PRODUCT_STATE_KEY.IS_EDIT_BASKET_LIMIT));
  const history = useHistory();
  const isMart = AuthHelper.getBusinessTypeId() === BUSINESS_TYPE.MART;
  const isIndividual = AuthHelper.getMerchantTypeId() === MERCHANT_TYPE.INDIVIDUAL;
  const selectedTagId = form.getFieldsValue(['tagId'])?.tagId;
  const [selectedTag, setSelectedTag] = useState<Tags>();
  const [isDeletedTag, setIsDeletedTag] = useState<boolean>(false);
  const [originalPrice, setOriginalPrice] = useState<string | undefined>(isNil(product?.originalProductPrice) ? undefined : numeral(product?.originalProductPrice).format('0,0.00'));
  const [priceDiscount, setDiscountPrice] = useState<string | undefined>(isNil(product?.productPrice) ? undefined : numeral(product?.productPrice).format('0,0.00'));
  const [promoStatus, setPromoStatus] = useState<string | undefined>(product?.promoStatus);
  const isActivePromoStatus = useMemo(() => {
    return promoStatus === PROMOTION_STATUS.ACTIVE;
  }, [promoStatus]);
  const isZeroOriginalPrice = useMemo(() => {
    return originalPrice === '0.00';
  }, [originalPrice]);
  const disablePromoStartDateTime: boolean = useMemo(() => {
    return isActivePromoStatus || originalPrice === '0.00' || !priceDiscount;
  }, [originalPrice, priceDiscount, isActivePromoStatus]);
  const disablePromoEndDateTime = useMemo(() => {
    return originalPrice === '0.00' || !priceDiscount;
  }, [originalPrice, priceDiscount]);
  const getNextDateTime = useCallback((time: moment.Moment) => {
    const nextTime = time.clone();
    const minute = time.get('m');
    if (minute >= 30) {
      nextTime.set({
        hour: time.get('h') + 1,
        minute: 0,
        second: 0,
        millisecond: 0
      });
    } else {
      nextTime.set({
        minute: 30,
        second: 0,
        millisecond: 0
      });
    }
    const result = nextTime.format('YYYY-MM-DD HH:mm').split(' ');
    return {
      date: moment(result[0]),
      time: result[1]
    };
  }, []);
  const periodDateMin = 90;
  const periodDateMax = 365;
  const [currentDate, setCurrentDate] = useState<moment.Moment>(moment());
  const [currentStartDate, setCurrentStartDate] = useState<moment.Moment>(product?.promoStartDate ? moment(product?.promoStartDate) : getNextDateTime(moment()).date);
  const [currentStartTime, setCurrentStartTime] = useState<string>(product?.promoStartTime || getNextDateTime(moment()).time);
  const maxStartDate = moment().add(periodDateMin, 'd');
  const maxEndDate = useMemo(() => {
    return moment(`${moment(currentStartDate).format('YYYY-MM-DD')}${!!currentStartTime ? ' ' + currentStartTime : ''}`).add(periodDateMax, 'd');
  }, [currentStartDate, currentStartTime]);
  const [currentEndDate, setCurrentEndDate] = useState<moment.Moment>(product?.promoEndDate ? moment(product?.promoEndDate) : getNextDateTime(maxEndDate).date);
  const [currentEndTime, setCurrentEndTime] = useState<string>(product?.promoEndTime || getNextDateTime(moment()).time);
  const [startDateTimeValidation, setStartDateTimeValidation] = useState<ValidateStatus>();
  const [endDateTimeValidation, setEndDateTimeValidation] = useState<ValidateStatus>();

  const disabledStartDate = (current: moment.Moment) => {
    const start = moment().subtract(1, 'd');
    const end = maxStartDate;
    return current < start || current > end;
  };
  const disabledEndDate = (current: moment.Moment) => {
    const start = isActivePromoStatus ? moment().subtract(1, 'd') : currentStartDate;
    const end = maxEndDate;
    return current < start || current > end;
  };

  useEffect(() => {
    if (props.editData) {
      form.validateFields();
    } else {
      dispatch(httpCreators.request({
        id: MART_CATEGORY_REQUEST_ID_LIST.GET_MART_CATEGORY,
        sagaHandler: martCategorySagas.getMartCategoryThenTncHandler as any,
        callbackParams: {
          onSuccessCallback: noop,
          onFailureCallback: () => {
            dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
              visible: true,
              type: MODAL_TYPE.ERROR,
              onOk: noop,
            }));
          },
        },
        service: MartCategoryService.getMartCategory,
      }));
    }
    return () => {
      if (history.action === 'POP' && props.editData) {
        history.push('/main/martproduct');
      }
    };
  }, [history]);

  useEffect(() => {
    dispatch(martProductCreators.setMartProductState(MART_PRODUCT_STATE_KEY.IS_EDIT_BASKET_LIMIT, !!props.editData));
    dispatch(martProductCreators.setMartProductState(MART_PRODUCT_STATE_KEY.PRODUCT_CALCULATED_WEIGHT, undefined));
    dispatch(martProductCreators.setMartProductState(MART_PRODUCT_STATE_KEY.PRODUCT_CALCULATED_SIZE, undefined));
    dispatch(martProductCreators.setMartProductState(MART_PRODUCT_STATE_KEY.PRODUCT_WEIGHT_SIZE_DATA, undefined));
  }, []);

  useEffect(() => {
    if (!!calculatedSize || !!calculatedWeight) {
      form.setFieldsValue({
        productDetailInput: calculatedWeight?.productWeightProductDisplayTh
          ? calculatedWeight?.productWeightProductDisplayTh
          : calculatedSize?.productSizeProductDisplayTh,
        productAmountPerOrder: (calculatedSize?.isProductUnlimit || calculatedWeight?.isProductUnlimit)
          ? 'ไม่จำกัดจำนวน'
          : calculatedSize?.purchasingAmountLimit || calculatedWeight?.purchasingAmountLimit
      });
    } else {
      form.resetFields(['productDetailInput', 'productAmountPerOrder']);
    }
    form.validateFields(['productDetailInput', 'productAmountPerOrder']);
  }, [calculatedSize, calculatedWeight]);

  useEffect(() => {
    if (isEditBasketLimit) {
      form.validateFields(['productDetailInput', 'productAmountPerOrder']);
    } else {
      form.resetFields(['productDetailInput', 'productAmountPerOrder']);
    }
  }, [isEditBasketLimit]);

  const findTagInMasterData = useCallback((tagId: number) => {
    return martTag?.find((value: Tags) => value.tagId === tagId)
  }, [martTag]);

  useEffect(() => {
    setSelectedTag(findTagInMasterData(selectedTagId));
  }, [martTag, selectedTagId]);

  const trimFieldValue = (fieldName: string) => {
    const value = form.getFieldValue(fieldName);
    if (value) {
      form.setFieldsValue({
        [fieldName]: value.trim()
      });
      form.validateFields([fieldName]);
    }
  };

  const clearPromoFormValues = useCallback((keepDiscountPrice?: boolean) => {
    if (!keepDiscountPrice) {
      setDiscountPrice(undefined);
      form.setFieldsValue({
        productPrice: undefined
      });
    }
    const startDateTime = getNextDateTime(moment());
    const endDateTime = getNextDateTime(moment().add(periodDateMax, 'd'));
    form.setFieldsValue({
      promoStartDate: startDateTime.date,
      promoStartTime: startDateTime.time,
      promoEndDate: endDateTime.date,
      promoEndTime: endDateTime.time,
    });
    setCurrentDate(moment());
    setCurrentStartDate(startDateTime.date);
    setCurrentStartTime(startDateTime.time);
    setCurrentEndDate(endDateTime.date);
    setCurrentEndTime(endDateTime.time);
    setStartDateTimeValidation('success');
    setEndDateTimeValidation('success');
    form.resetFields(['promoStartDateTime', 'promoEndDateTime']);
  }, [])

  const onBlurProductPriceInput = () => {
    const value = form.getFieldValue('productPrice');
    // not auto fill zero because discount price is optional
    if (isNaN(value) || value === '' || value < 0) {
      clearPromoFormValues();
    } else {
      const formatPrice = UtilHelper.currencyFormat(value, isMart);
      form.setFieldsValue({
        productPrice: formatPrice
      });
      setDiscountPrice(formatPrice);
    }
    if (props.editData && isActivePromoStatus && numeral(value).value() !== product?.productPrice) {
      setPromoStatus(PROMOTION_STATUS.PENDING);
      clearPromoFormValues(true);
    }
    validateFields(['productPrice']);
  };

  const onFocusProductPriceInput = () => {
    const value = form.getFieldValue('productPrice');
    form.setFieldsValue({
      productPrice: UtilHelper.removeComma(value)
    });
  };

  const onBlurOriginalProductPriceInput = () => {
    const value = form.getFieldValue('originalProductPrice');
    const formatValue = UtilHelper.currencyFormat(value, isMart);
    form.setFieldsValue({
      originalProductPrice: formatValue
    });
    setOriginalPrice(formatValue);
    if (formatValue === '0.00') {
      clearPromoFormValues();
    }
    validateFields(['productPrice'])
  };

  const onFocusOriginalProductPriceInput = () => {
    const value = form.getFieldValue('originalProductPrice');
    form.setFieldsValue({
      originalProductPrice: UtilHelper.removeComma(value)
    });
  };

  const handleChangePhoto = (file: any) => {

    if (file.target.files[0] && (file.target.files[0].type === 'image/png' || file.target.files[0].type === 'image/jpeg')) {
      setLastFile(file.target.files);
      crop(URL.createObjectURL(file.target.files[0]), 1).then((canvas: any) => {
        setImageUpload(canvas);
      });
    } else {
      if (lastFile) {
        inputRef.current.files = lastFile;
      } else {
        inputRef.current.value = '';
      }
    }
  };

  const { Option } = Select;
  const getTagOption = () => {
    if (martTag && martTag.length > 0) {
      return martTag
        .filter((result: Tags) => !result.isDeleted)
        .map((result: Tags, index: number) => {
          return (
            <Option
              key={index}
              id={`martTagOption-${index}`}
              data-testid={`martTagOption-${index}`}
              value={result.tagId}
              style={styleText.textRegular14}
              title={result.tagTh}
            >
              {result.tagTh}
            </Option>
          )
        });
    } else {
      return null;
    }
  };

  const getSelectOption = () => {
    if (martCategory && martCategory.length > 0) {
      return martCategory.map((result: any, index: number) => {
        return (
          result.productCategoryGroupingId === 1 ?
            <Option
              key={index}
              id={`categoryOption-${index}`}
              data-testid={`categoryOption-${index}`}
              value={result.productCategoryId}
              style={styles.option_promotion}
              title={result.productCategoryNameTH}
            >
              <img src={Images.promotion} style={styleImage.iconS} />
              {result.productCategoryNameTH}
            </Option>
            :
            <Option
              key={index}
              id={`categoryOption-${index}`}
              data-testid={`categoryOption-${index}`}
              value={result.productCategoryId}
              style={styleText.textRegular14}
              title={result.productCategoryNameTH}
            >
              {result.productCategoryNameTH}
            </Option>
        );
      });
    }
    return null;
  };

  const disabledStartTimeIndexes = useMemo(() => {
    if (moment(currentStartDate).isSame(currentDate, 'day')) {
      const startDateTime = getNextDateTime(currentDate);
      const currentIndex = TIME_24.findIndex((value) => value === startDateTime.time);
      return range(currentIndex);
    }
    return [];
  }, [currentDate, currentStartDate]);

  const disabledEndTimeIndexes = useMemo(() => {
    if (props.editData && isActivePromoStatus && moment(currentEndDate).isSame(moment(), 'day')) {
      const nextEndDateTime = getNextDateTime(moment());
      const currentIndex = TIME_24.findIndex((value) => value === nextEndDateTime.time);
      return range(currentIndex);
    } else if (moment(currentEndDate).isSame(maxEndDate, 'day')) {
      const currentIndex = TIME_24.findIndex((value) => value === currentStartTime);
      return range(currentIndex + 1, TIME_24.length);
    } else if (moment(currentEndDate).isSame(currentStartDate, 'day')) {
      const currentIndex = TIME_24.findIndex((value) => value === currentStartTime);
      return range(currentIndex + 1);
    } else if (moment(currentEndDate).isBefore(currentStartDate, 'day')) {
      return range(0, TIME_24.length);
    } else if (moment(currentEndDate).isAfter(maxEndDate, 'day')) {
      return range(0, TIME_24.length);
    }
    return [];
  }, [maxEndDate, currentEndDate, currentStartDate, currentStartTime, isActivePromoStatus]);

  const getTimeOption = (start: boolean) => {
    return TIME_24.map((time, index) => {
      const disabled = start ? disabledStartTimeIndexes.includes(index) : disabledEndTimeIndexes.includes(index);
      return (
        <Option
          style={disabled ? styles.timeOptionDisabled : styles.timeOption}
          id={`${start ? 'start' : 'end'}-time-${index}`}
          key={index}
          value={time}
          disabled={disabled}>
          {time}
        </Option>
      );
    });
  }

  const validatePromoDateTime = (startDate: moment.Moment, startTime: string, endDate: moment.Moment, endTime: string) => {
    const startDateTime = moment(startDate?.format('YYYY-MM-DD') + ' ' + startTime);
    const endDateTime = moment(endDate?.format('YYYY-MM-DD') + ' ' + endTime);

    setStartDateTimeValidation('success');
    setEndDateTimeValidation('success');
    form.resetFields(['promoStartDateTime', 'promoEndDateTime']);

    if (!(props.editData && isActivePromoStatus) && startDateTime.isBefore(moment())) {
      setStartDateTimeValidation('error');
      form.setFields([{
        name: ['promoStartDateTime'],
        errors: [MART_PRODUCT_INLINE_ERROR.PROMO_START_DATETIME_INVALID]
      }]);
    }
    if (endDateTime.isBefore(moment())) {
      setEndDateTimeValidation('error');
      form.setFields([{
        name: ['promoEndDateTime'],
        errors: [MART_PRODUCT_INLINE_ERROR.PROMO_END_DATETIME_INVALID]
      }]);
    }
    if (endDateTime.isSame(startDateTime)) {
      setEndDateTimeValidation('error');
      form.setFields([{
        name: ['promoEndDateTime'],
        errors: [MART_PRODUCT_INLINE_ERROR.PROMO_SAME_DATETIME]
      }]);
    }
    if (endDateTime.isBefore(startDateTime)) {
      setStartDateTimeValidation('error');
      setEndDateTimeValidation('error');
      form.setFields([
        {
          name: ['promoStartDateTime'],
          errors: [MART_PRODUCT_INLINE_ERROR.PROMO_START_DATETIME_INVALID]
        },
        {
          name: ['promoEndDateTime'],
          errors: [MART_PRODUCT_INLINE_ERROR.PROMO_END_DATETIME_INVALID]
        }]);
    }
    if (endDateTime.isAfter(moment(`${moment(startDate).format('YYYY-MM-DD')} ${startTime}`).add(periodDateMax, 'd'))) {
      setEndDateTimeValidation('error');
      form.setFields([{
        name: ['promoEndDateTime'],
        errors: [MART_PRODUCT_INLINE_ERROR.PROMO_END_DATETIME_EXCEED]
      }]);
    }
  }

  const validateSku = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    dispatch(martProductCreators.setMartProductState(MART_PRODUCT_STATE_KEY.IS_POS_ACTIONED, true));
    form.validateFields(['productSKUId']);
  };

  const setFieldError = (fieldName: string, errorShow: string[]) => {
    form.setFields([{
      name: fieldName,
      errors: errorShow
    }]);
    const fieldError = form.getFieldsError();
    const errorField = fieldError.find((e: any) => e.errors[0] === MART_PRODUCT_INLINE_ERROR.PRODUCT_NAME_DUP);
    form.scrollToField(`${errorField?.name}`, { behavior: 'smooth' });
    if (fieldName === 'promoStartDateTime') {
      setStartDateTimeValidation('error');
    } else if (fieldName === 'promoEndDateTime') {
      setEndDateTimeValidation('error');
    }
    setFormInvalid(true);
  };

  const onFinish = (values: any) => {
    const formData = form.getFieldsValue();
    let productWeightProduct;
    if (!!productWeightSizeData) {
      productWeightProduct = productWeightSizeData?.productWeightProduct;
    } else {
      if (props?.editData?.productWeightProduct === null) {
        productWeightProduct = null;
      } else {
        const productWeightValue = productWeightSizeData?.productWeightProduct
          ? productWeightSizeData?.productWeightProduct.productWeightValue
          : props?.editData?.productWeightProduct?.productWeightValue;
        const productWeightUnitId = productWeightSizeData?.productWeightProduct
          ? productWeightSizeData?.productWeightProduct.productWeightUnitId
          : props?.editData?.productWeightProduct?.productWeightUnitId;
        productWeightProduct = {
          productWeightValue,
          productWeightUnitId,
        };
      }
    }
    let productSizeProduct;
    if (!!productWeightSizeData) {
      productSizeProduct = productWeightSizeData?.productSizeProduct;
    } else {
      if (props?.editData?.productSizeProduct === null) {
        productSizeProduct = null;
      } else {
        const productSizeId = productWeightSizeData?.productSizeProduct
          ? productWeightSizeData?.productSizeProduct?.productSizeId
          : props?.editData?.productSizeProduct?.productSizeId;
        productSizeProduct = {
          productSizeId
        };
      }
    }
    let productPerpackAmount;
    if (productWeightSizeData) {
      productPerpackAmount = productWeightSizeData?.productPerpackProduct?.productPerpackAmount;
    } else {
      productPerpackAmount = props?.editData?.productPerpackProduct?.productPerpackAmount;
    }
    const productPerpackProduct = {
      productPerpackAmount
    };
    let productPrice;
    let promoStartDateTime;
    let promoEndDateTime;
    if (!values.productPrice) {
      productPrice = numeral(values.originalProductPrice).value();
    } else {
      productPrice = numeral(values.productPrice).value();
      promoStartDateTime = `${moment(values.promoStartDate).format('YYYY-MM-DD')}T${values.promoStartTime}:00+07:00`;
      promoEndDateTime = `${moment(values.promoEndDate).format('YYYY-MM-DD')}T${values.promoEndTime}:00+07:00`;
    }
    const data = {
      tagId: values.tagId,
      productId: props.editData ? Number(productId) : undefined,
      productCategoryId: props.editData ? (
        values.productCategoryId === category ? productCategoryId : Number(formData.productCategoryId)
      ) : values.productCategoryId,
      productNameTH: values.productNameTH,
      productDescriptionTH: values.productDescriptionTH,
      productNameEN: values.productNameEN,
      productDescriptionEN: values.productDescriptionEN,
      originalProductPrice: numeral(values.originalProductPrice).value(),
      productPrice,
      promoStartDateTime,
      promoEndDateTime,
      productPhoto: imageUpload,
      productStatus: productStatus ? MART_PRODUCT_STATUS.ACTIVE : MART_PRODUCT_STATUS.INACTIVE,
      productSKUId: values.productSKUId,
      productWeightProduct,
      productSizeProduct,
      productPerpackProduct
    };

    const clearFieldTagIsDeleted = () => {
      setIsDeletedTag(true);
      form.resetFields(['tagId']);
      setFieldError('tagId', [MART_PRODUCT_INLINE_ERROR.TAG_NOT_FOUND]);
    };

    if (props.editData) {
      if (imageUpload === props.editData.productPhoto) {
        delete data.productPhoto;
      }
      dispatch(httpCreators.request({
        id: MART_PRODUCT_REQUEST_ID_LIST.PATCH_MART_PRODUCT,
        sagaHandler: martProductSagas.postPatchMartProductHandler as any,
        callbackParams: { onSuccessCallback: props.onSuccessCallback, validateSku, setFieldError, clearFieldTagIsDeleted },
        service: MartProductService.patchMartProduct,
        serviceParams: data,
      }));
    } else {
      dispatch(httpCreators.request({
        id: MART_PRODUCT_REQUEST_ID_LIST.POST_MART_PRODUCT,
        sagaHandler: martProductSagas.postPatchMartProductHandler as any,
        callbackParams: { onSuccessCallback: props.onSuccessCallback, validateSku, setFieldError, clearFieldTagIsDeleted },
        service: MartProductService.postMartProduct,
        serviceParams: data,
      }));
    }
  };

  const checkFormInvalid = () => {
    let invalid = false;
    const values = form.getFieldsValue();
    for (const key in values) {
      if (!(key === 'productDescriptionTH' || key === 'productNameEN' || key === 'productDescriptionEN' || key === 'photoURL' ||
        (key === 'productSKUId' && !isPOSActioned) || key === 'productPrice' || key === 'promoStartDateTime' || key === 'promoEndDateTime')) {
        if (values[key] === undefined || values[key] === '') {
          invalid = true;
          break;
        }
      }
    }

    if (!invalid) {
      const errors = form.getFieldsError();
      for (const field of errors) {
        if (field.errors.length > 0) {
          invalid = true;
          break;
        }
      }
    }
    setFormInvalid(invalid);
  };

  const textTipsName = () => {
    return (
      <>
        <pre id='nameTooltipContainer' style={mainStyles.box_name_Tooltip}>
          <div id='nameTooltip'><FormattedMessage id={'main.mart.product.add.form.product.tips'} /></div>
        </pre>
      </>
    );
  };

  const textTipsPrices = () => {
    return (
      <a id='priceTooltip' style={mainStyles.box_prices_Tooltip}>
        <FormattedMessage id={'main.mart.product.add.form.price.tips'} />
      </a>
    );
  };

  const textOptional = (id: string) => {
    return (
      <RBHBox style={{ display: 'flex' }}>
        <RBHText data-testid={id} style={styleText.formText} text={id} />&nbsp;
        <RBHText data-testid={'ifOptional'} style={styleText.textRegular14Gray} text={'main.form.text.optional'} />
      </RBHBox>
    );
  };

  const dispatchModal = (path: any) => {
    dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: () => history.push(path),
      type: MODAL_TYPE.CONFIRM_EDIT
    }));
  };

  const validateFields = (fieldName: string[]) => {
    form.validateFields([fieldName]);
  };

  const getBasketLimit = () => {
    dispatch(httpCreators.request({
      service: MartProductService.getBasketLimit,
      id: MART_PRODUCT_REQUEST_ID_LIST.GET_BASKET_LIMIT,
      sagaHandler: martProductSagas.getBasketLimitHandler as any,
      callbackParams: {
        productCharacteristic: {
          piecesPerPackOption: productWeightSizeData
            ? productWeightSizeData?.productPerpackProduct?.productPerpackAmount
            : props?.editData?.productPerpackProduct?.productPerpackAmount,
          weightLimitOption1Input: productWeightSizeData
            ? productWeightSizeData?.productWeightProduct?.productWeightValue
            : props?.editData?.productWeightProduct?.productWeightValue,
          weightLimitSelect: productWeightSizeData
            ? productWeightSizeData?.productWeightProduct
            : props?.editData?.productWeightProduct,
          sizeLimitSelect: productWeightSizeData
            ? productWeightSizeData?.productSizeProduct
            : props?.editData?.productSizeProduct,
          weightUnitId: productWeightSizeData
            ? productWeightSizeData?.productWeightProduct?.productWeightUnitId
            : props?.editData?.productWeightProduct?.productWeightUnitId,
          sizeLimitId: productWeightSizeData
            ? productWeightSizeData?.productSizeProduct?.productSizeId
            : props?.editData?.productSizeProduct?.productSizeId,
        },
        afterClose: () => {
          validateFields(['productDetailInput']);
        },
      }
    }));
  };

  return (
    <Form size='large' style={styleText.formText} {...layout} form={form} onFinish={onFinish} onFieldsChange={checkFormInvalid}>
      {
        isIndividual && (
          <Form.Item
            colon={false} required={true} label={<FormattedMessage id={'main.mart.product.add.form.tag.title'} />}
            extra={
              !!selectedTag?.remark && (
                <RBHText
                  data-testid={'tagPickerRemark'}
                  id={'tagPickerRemark'}
                  style={styles.tagPickerRemark}
                >
                  {selectedTag?.remark}
                </RBHText>
              )
            }
          >
            <Form.Item
              noStyle
              name='tagId'
              initialValue={product ? product.tagId : undefined}
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id={'main.form.required2'} />
                },
                () => ({
                  validator(_, formValue) {
                    const currentTag = findTagInMasterData(formValue);
                    if (currentTag?.isDeleted) {
                      setIsDeletedTag(true);
                      return Promise.reject(<FormattedMessage id={'main.mart.product.tag.isDeleted'} />);
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <RBHSelect
                id={'martTagPicker'}
                data-testid={'martTagPicker'}
                optionFilterProp='children'
                showSearch
                placeholder={isDeletedTag ? 'main.mart.product.add.form.tag.isDeleted.placeholder' : 'main.mart.product.add.form.tag.placeholder'}
                defaultValue={isDeletedTag ? undefined : product?.tag?.tagTh}
                onChange={(tagId: number) => {
                  setSelectedTag(findTagInMasterData(tagId));
                  setIsDeletedTag(false);
                  form.validateFields(['tagId'])
                  form.setFieldsValue({
                    'tagId': tagId
                  });
                }}
              >
                {getTagOption()}
              </RBHSelect>

              {/* magic re-render */}
              <div />
            </Form.Item>
          </Form.Item>
        )
      }

      <Form.Item colon={false} required={true} label={<FormattedMessage id={'main.mart.product.add.form.category.title'} />}>
        <Form.Item noStyle name='productCategoryId'
          rules={[{ required: true }]}
          initialValue={product ? product.productCategory?.productCategoryNameTH : undefined}
        >
          <RBHSelect id='categoryPicker' optionFilterProp='children' showSearch placeholder='main.mart.product.add.form.category.placeholder'>
            {getSelectOption()}
          </RBHSelect>
        </Form.Item>
        <span id='addCategoryLink' style={styles.addCategoryText} onClick={() => dispatchModal('/main/addmartcategory')}>
          <span>+ </span>
          <span style={styles.addCategoryTextInner}><FormattedMessage id={'main.mart.product.add.form.category.addCategory'} /></span>
        </span>
      </Form.Item>

      <Tooltip id='tooltip-product-name' placement='bottom' title={textTipsName} trigger={'focus'}>
        <Form.Item colon={false} validateTrigger={['onChange', 'onBlur']} name='productNameTH'
          label={<FormattedMessage id={'main.mart.product.add.form.product.title'} />}
          rules={[
            { required: true, message: <FormattedMessage id='main.form.required' /> },
            { pattern: MenuValidator.menuName.regexPattern, message: <FormattedMessage id='main.form.text.valid' /> },
            { max: MenuValidator.menuName.maxLength, message: <FormattedMessage id='main.form.maxlength' /> }]}
          initialValue={product ? product.productNameTH : undefined}>
          <RBHTextInput
            id='productNameInput'
            onBlur={() => trimFieldValue('productNameTH')}
            maxLength={MenuValidator.menuName.maxLength}
            placeholder={'main.mart.product.add.form.product.placeholder'}
            autoComplete='off'
          />
        </Form.Item>
      </Tooltip>

      <Form.Item colon={false} name='productDescriptionTH' label={textOptional('main.mart.product.add.form.description.title')}
        initialValue={product ? product.productDescriptionTH : ''} validateTrigger={['onChange', 'onBlur']}
        rules={[
          { pattern: MenuValidator.menuDescription.regexPattern, message: <FormattedMessage id='main.form.text.valid' /> }]}>
        <RBHTextInputArea id='productDescInput' placeholder={'main.mart.product.add.form.description.placeholder'}
          onBlur={() => trimFieldValue('productDescriptionTH')} maxLength={MenuValidator.menuDescription.maxLength} />
      </Form.Item>

      <Form.Item colon={false} name='productNameEN' label={textOptional('main.mart.product.add.form.product.titleEN')}
        initialValue={product ? product.productNameEN : ''} validateTrigger={['onChange', 'onBlur']}
        rules={[
          { pattern: MenuValidator.menuNameEN.regexPattern, message: <FormattedMessage id='main.form.text.validEN' /> }]}>
        <RBHTextInput id='productNameEngInput' placeholder={'main.mart.product.add.form.product.placeholderEN'}
          onBlur={() => trimFieldValue('productNameEN')} maxLength={MenuValidator.menuNameEN.maxLength} autoComplete='off' />
      </Form.Item>

      <Form.Item colon={false} name='productDescriptionEN' label={textOptional('main.mart.product.add.form.description.titleEN')}
        initialValue={product ? product.productDescriptionEN : ''} validateTrigger={['onChange', 'onBlur']}
        rules={[
          { pattern: MenuValidator.menuDescriptionEN.regexPattern, message: <FormattedMessage id='main.form.text.validEN' /> }]}
        extra={<RBHBox>
          <RBHText data-testid={'productRemarkEng'} id={'productRemarkEng'} style={styles.remark}
            text={'main.mart.product.add.form.description.remarkEN'} />
        </RBHBox>}>
        <RBHTextInputArea id='productDescEngInput' placeholder={'main.mart.product.add.form.description.placeholderEN'}
          onBlur={() => trimFieldValue('productDescriptionEN')} maxLength={MenuValidator.menuDescriptionEN.maxLength} />
      </Form.Item>

      {
        isPOSActioned ?
          <Form.Item colon={false} required={isPOSActioned} name={'productSKUId'} initialValue={product ? product.productSKUId : ''}
            label={
              <RBHBox>
                <RBHText id={'productSKUIdLabel'} data-testid={'productSKUIdLabel'}
                  style={styleText.formText} text={'main.mart.product.add.form.SKU.title'} />
              </RBHBox>
            }
            rules={[
              { required: true, message: <FormattedMessage id={'main.form.required'} /> },
              { pattern: MenuValidator.menuSKUId.regexPattern, message: <FormattedMessage id={'main.form.text.valid'} /> }]}
            validateTrigger={['onChange', 'onBlur']}>
            <RBHTextInput id={'productSKUIdInput'} placeholder={'main.mart.product.add.form.SKU.placeholder'} style={{ fontSize: 13 }}
              onBlur={() => trimFieldValue('productSKUId')} maxLength={MenuValidator.menuSKUId.maxLength} autoComplete={'off'} />
          </Form.Item>
          :
          <Form.Item colon={false} name={'productSKUId'} label={textOptional('main.mart.product.add.form.SKU.title')}
            initialValue={product ? product.productSKUId : ''}
            rules={[
              { pattern: MenuValidator.menuSKUId.regexPattern, message: <FormattedMessage id='main.form.text.valid' /> }]}>
            <RBHTextInput id={'productSKUIdInput'} placeholder={'main.mart.product.add.form.SKU.placeholder'} style={{ fontSize: 13 }}
              onBlur={() => trimFieldValue('productSKUId')} maxLength={MenuValidator.menuSKUId.maxLength} autoComplete={'off'} />
          </Form.Item>
      }

      <Tooltip id={'tooltip-original-product-price'} placement={'bottom'} title={textTipsPrices} trigger={'focus'}>
        <Form.Item colon={false} validateTrigger={['onChange', 'onBlur']} name='originalProductPrice'
          label={<FormattedMessage id={'main.mart.product.add.form.originalPrice.title'} />}
          rules={[
            { required: true, message: <FormattedMessage id='main.form.required' /> },
            {
              pattern: PRODUCT_PRICE_REGEX,
              message: <FormattedMessage id={'main.mart.product.add.form.price.validate'} />
            }
          ]}
          initialValue={product ? numeral(product.originalProductPrice).format('0,0.00') : undefined}>
          <RBHTextInput
            id={'originalProductPriceInput'}
            data-testid={'originalProductPriceInput'}
            style={styleTextInput.inputWidth150}
            placeholder={'main.mart.product.add.form.price.placeholder'}
            maxLength={PRODUCT_PRICE_MAXLENGTH}
            autoComplete={'off'}
            onFocus={onFocusOriginalProductPriceInput}
            onBlur={onBlurOriginalProductPriceInput}
          />
        </Form.Item>
      </Tooltip>

      <Tooltip id={'tooltip-discount-product-price'} placement={'bottom'} title={textTipsPrices} trigger={'focus'}>
        <Form.Item colon={false} validateTrigger={['onChange', 'onBlur']} name='productPrice'
          label={textOptional('main.mart.product.add.form.priceDiscount.title')}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, formValue) {
                const currentPrice = numeral(formValue).value();
                const currentOriginalPrice = numeral(getFieldValue('originalProductPrice')).value();
                if (!!currentPrice && !!currentOriginalPrice && currentPrice >= currentOriginalPrice) {
                  return Promise.reject(<FormattedMessage id={'main.mart.product.form.error.productPrice'} />);
                } else if (!isNil(formValue) && formValue !== '' && !PRODUCT_PRICE_REGEX.test(formValue)) {
                  return Promise.reject(<FormattedMessage id={'main.mart.product.add.form.price.validate'} />)
                }
                return Promise.resolve();
              }
            })
          ]}
          initialValue={priceDiscount}>
          <RBHTextInput
            id={'discountProductPriceInput'}
            data-testid={'discountProductPriceInput'}
            style={styleTextInput.inputWidth150}
            placeholder={'main.mart.product.add.form.price.placeholder'}
            maxLength={PRODUCT_PRICE_MAXLENGTH}
            autoComplete={'off'}
            onFocus={onFocusProductPriceInput}
            onBlur={onBlurProductPriceInput}
            disabled={isZeroOriginalPrice}
          />
        </Form.Item>
      </Tooltip>

      <ConfigProvider locale={locale}>
        <Form.Item
          name={'promoStartDateTime'}
          label={<FormattedMessage id={'main.mart.product.add.form.startDateTime.title'} />}
          colon={false}
        >
          <Form.Item
            name={'promoStartDate'}
            style={styles.dateTimeInput}
            initialValue={currentStartDate}
            validateTrigger={['onChange', 'onBlur']}
            validateStatus={startDateTimeValidation}
            rules={[
              () => ({
                validator(_, startDate) {
                  validatePromoDateTime(startDate, currentStartTime, currentEndDate, currentEndTime);
                  return Promise.resolve();
                }
              })
            ]}
          >
            <RBHDatePicker
              allowClear={false}
              showToday={true}
              name={'promoStartDate'}
              style={styles.calendarInput}
              data-testid={'promoStartDate'}
              id={'promoStartDate'}
              format={DATE_PICKER_FORM}
              onChange={(date: moment.Moment) => setCurrentStartDate(date)}
              autoComplete={'off'}
              disabledDate={disabledStartDate}
              disabled={disablePromoStartDateTime}
            />
          </Form.Item>
          <Form.Item
            name={'promoStartTime'}
            style={styles.dateTimeInput}
            initialValue={currentStartTime}
            validateTrigger={['onChange', 'onBlur']}
            validateStatus={startDateTimeValidation}
            rules={[
              () => ({
                validator(_, startTime) {
                  validatePromoDateTime(currentStartDate, startTime, currentEndDate, currentEndTime);
                  return Promise.resolve();
                }
              })
            ]}
          >
            <RBHSelect
              id={'promoStartTime'}
              data-testid={'promoStartTime'}
              optionFilterProp={'children'}
              style={styles.selectTime}
              dropdownStyle={{ textAlign: 'center' }}
              // add pointer event to fix cannot click on icon
              suffixIcon={<ClockCircleOutlined translate={null} style={{ pointerEvents: 'none' }} />}
              onChange={(time: string) => setCurrentStartTime(time)}
              onFocus={() => setCurrentDate(moment())}
              disabled={disablePromoStartDateTime}
            >
              {getTimeOption(true)}
            </RBHSelect>
          </Form.Item>
        </Form.Item>
        <Form.Item
          name={'promoEndDateTime'}
          label={<FormattedMessage id={'main.mart.product.add.form.endDateTime.title'} />}
          colon={false}
        >
          <Form.Item
            name={'promoEndDate'}
            style={styles.dateTimeInput}
            initialValue={currentEndDate}
            validateTrigger={['onChange', 'onBlur']}
            validateStatus={endDateTimeValidation}
            rules={[
              () => ({
                validator(_, endDate) {
                  validatePromoDateTime(currentStartDate, currentStartTime, endDate, currentEndTime);
                  return Promise.resolve();
                }
              })
            ]}
          >
            <RBHDatePicker
              allowClear={false}
              showToday={true}
              name={'promoEndDate'}
              style={styles.calendarInput}
              data-testid={'promoEndDate'}
              id={'promoEndDate'}
              format={DATE_PICKER_FORM}
              onChange={(date: moment.Moment) => setCurrentEndDate(date)}
              autoComplete={'off'}
              disabledDate={disabledEndDate}
              disabled={disablePromoEndDateTime}
            />
          </Form.Item>
          <Form.Item
            name={'promoEndTime'}
            style={styles.dateTimeInput}
            initialValue={currentEndTime}
            validateTrigger={['onChange', 'onBlur']}
            validateStatus={endDateTimeValidation}
            rules={[
              () => ({
                validator(_, endTime) {
                  validatePromoDateTime(currentStartDate, currentStartTime, currentEndDate, endTime);
                  return Promise.resolve();
                }
              })
            ]}
          >
            <RBHSelect
              id={'promoEndTime'}
              data-testid={'promoEndTime'}
              optionFilterProp={'children'}
              style={styles.selectTime}
              dropdownStyle={{ textAlign: 'center' }}
              // add pointer event to fix cannot click on icon
              suffixIcon={<ClockCircleOutlined translate={null} style={{ pointerEvents: 'none' }} />}
              onChange={(time: string) => setCurrentEndTime(time)}
              onFocus={() => setCurrentDate(moment())}
              disabled={disablePromoEndDateTime}
            >
              {getTimeOption(false)}
            </RBHSelect>
          </Form.Item>
        </Form.Item>
      </ConfigProvider>

      <Form.Item className={'productDetailInput'} colon={false} validateTrigger={['onChange', 'onBlur']} name={'productDetailInput'}
        label={<FormattedMessage id={'main.mart.product.add.form.detail.title'} />}
        rules={[
          { required: true, message: <FormattedMessage id={'main.mart.product.add.form.detail.error'} /> }
        ]}
        initialValue={product
          ? product?.productWeightProduct
            ? product?.productWeightProduct?.productWeightProductDisplayTh
            : product?.productSizeProduct?.productSizeProductDisplayTh
          : undefined
        }
      >
        <RBHTextInput id={'productDetailInput'} data-testid={'productDetailInput'} style={styleTextInput.inputDefault}
          placeholder={'main.mart.product.add.form.detail.placeholder'} disabled={true} />
      </Form.Item>

      <RBHBox style={styles.detailProductBtnBox}>
        <RBHButton style={productWeightSizeData || props.editData ? styles.editDetailProduct : styles.selectDetailProduct} type={'primary'} htmlType={'button'}
          id={'productDetailButton'} data-testid={'productDetailButton'} onClick={getBasketLimit}>
          <FormattedMessage id={productWeightSizeData || props.editData ? 'main.mart.product.add.form.detail.btn.edit' : 'main.mart.product.add.form.detail.btn.add'} />
        </RBHButton>
      </RBHBox>

      <Form.Item colon={false} validateTrigger={['onChange', 'onBlur']} name={'productAmountPerOrder'}
        label={<FormattedMessage id={'main.mart.product.add.form.amountPerOrder.title'} />}
        initialValue={product
          ? product?.productPurchasingAmountLimit?.isProductUnlimit
            ? 'ไม่จำกัดจำนวน'
            : product?.productPurchasingAmountLimit?.purchasingAmountLimit
          : undefined
        }
        extra={
          <RBHBox>
            <RBHText data-testid={'productAmountPerOrderRemark'} id={'productAmountPerOrderRemark'} style={styles.remark}
              text={'main.mart.product.add.form.amountPerOrder.remark'} />
          </RBHBox>
        }
      >
        <RBHTextInput id={'productAmountPerOrder'} data-testid={'productAmountPerOrder'} style={styleTextInput.inputDefault}
          placeholder={'main.mart.product.add.form.amountPerOrder.placeholder'} disabled={true} />
      </Form.Item>

      <Form.Item name='photoURL' colon={false} label={textOptional('main.mart.product.add.form.picture.title')}
        initialValue={product ? product.productPhoto : undefined}>
        <RBHBox>
          <Row justify='start' align='middle'>
            <Space size={'middle'}>
              <RBHBox>
                <RBHImage id='pictureUploadImage' name='image_upload' style={mainStyles.imageBox} src={imageUpload || Images.productDefault2} />
              </RBHBox>
              <RBHBox style={mainStyles.uploadBox}>
                <label>
                  <input ref={inputRef} id='pictureUploadInput' accept='image/png, image/jpeg, image/jpg' type='file'
                    onChange={(e: any) => handleChangePhoto(e)} />
                  <RBHBox style={mainStyles.upload}>
                    <RBHText id='pictureUploadBrowse' style={styleText.text16LightPurple} text={'main.mart.product.add.form.picture.browse'} />
                  </RBHBox>
                </label>
              </RBHBox>
            </Space>
          </Row>
          <RBHBox style={mainStyles.paddingWarning}>
            <RBHText id='pictureUploadRemark' style={styleText.textRegular14Gray} text={'main.mart.product.add.form.picture.remark'} />
          </RBHBox>
        </RBHBox>
      </Form.Item>

      <Form.Item colon={false} label={<FormattedMessage id='main.mart.product.add.form.status.title' />}>
        <RBHBox style={mainStyles.displayinline}>
          <RBHSwitch id={'statusSwitch'} data-testid={'statusSwitch'} size={'small'}
            defaultChecked={productStatus} onChange={() => setproductStatus(!productStatus)}
          />
        </RBHBox>
        <RBHBox style={mainStyles.displayinline_left_13px}>
          {productStatus ?
            <RBHText id='statusLabel' style={styleText.formText} text={'main.mart.product.add.form.status.true'} /> :
            <RBHText id='statusLabel' style={styleText.textRegular14Gray} text={'main.mart.product.add.form.status.false'} />}
        </RBHBox>
      </Form.Item>

      <RBHText style={styles.redRemark}
        text={'main.mart.product.add.form.cannabis.remark'}
        id={'cannabisRemark'}
        data-testid={'cannabisRemark'}
      />

      {/* // FIXME: Refactor layout along with all views in this screen */}
      <Space size={'middle'}>
        <RBHBox>
        </RBHBox>
      </Space>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Space size={'middle'}>
          <RBHButton style={styleButton.buttonModalCancelGradient} id='cancelButton' type='primary' htmlType='button'
            onClick={() => {
              props.onCancel('/main/martproduct');
            }}>
            <FormattedMessage id='main.mart.product.add.form.action.cancel' />
          </RBHButton>
          {!product ?
            <RBHButton style={styleButton.buttonModalConfirmGradient} id={'submitButton'} data-testid={'submitButton'} type={'primary'} htmlType={'submit'}
              disabled={formInvalid}>
              <FormattedMessage id='main.mart.product.add.form.action.submit' />
            </RBHButton> : null}
          {product ?
            <RBHButton disabled={formInvalid} id={'saveButton'} data-testid={'saveButton'} style={styleButton.buttonModalConfirmGradient} type={'primary'}
              htmlType={'submit'} text='main.mart.product.edit.form.action.save' /> : null}
        </Space>
      </div>
    </Form>
  );
};

type Props = {
  onSuccessCallback: any,
  onCancel?: any,
  editData?: any
};

